import type { FieldExplainerContent } from './types';

export type PaycheckExplainerContext = {
  input: {
    salary: number;
    payFrequency: 'weekly' | 'biweekly' | 'semimonthly' | 'monthly';
    filingStatus: 'single' | 'married' | 'head';
    stateRate: number;
    contribution401kPercent: number;
    healthInsurance: number;
    hsa: number;
    otherPreTax: number;
  };
  result: {
    periodsPerYear: number;
    grossPayPerPeriod: number;
    contribution401kAnnual: number;
    contribution401kPerPeriod: number;
    preTaxDeductionsPerPeriod: number;
    taxableIncomeAnnual: number;
    standardDeduction: number;
    federalTaxableIncomeAnnual: number;
    federalMarginalRate: number;
    federalBracketLowerBound: number;
    federalBracketUpperBound: number;
    federalTaxAnnual: number;
    federalTaxPerPeriod: number;
    stateTaxAnnual: number;
    stateTaxPerPeriod: number;
    socialSecurityAnnual: number;
    socialSecurityPerPeriod: number;
    medicareAnnual: number;
    medicarePerPeriod: number;
    additionalMedicareThreshold: number;
    totalTaxesPerPeriod: number;
    netPayPerPeriod: number;
    annualNetPay: number;
  };
};

/** Employee elective deferral cap the engine applies to the 401(k) percentage. */
const DEFERRAL_CAP = 23500;
/** Wage base the engine caps Social Security at. */
const SOCIAL_SECURITY_WAGE_BASE = 176100;
const SOCIAL_SECURITY_RATE = 0.062;
const MEDICARE_RATE = 0.0145;
const ADDITIONAL_MEDICARE_RATE = 0.009;
/** The bracket table, standard deductions, and caps in the engine are 2025 figures. */
const TAX_YEAR = 2025;

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const usdWhole = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

const FREQUENCY_LABEL: Record<PaycheckExplainerContext['input']['payFrequency'], string> = {
  weekly: 'weekly',
  biweekly: 'biweekly',
  semimonthly: 'semimonthly',
  monthly: 'monthly'
};

const FILING_LABEL: Record<PaycheckExplainerContext['input']['filingStatus'], string> = {
  single: 'single',
  married: 'married filing jointly',
  head: 'head of household'
};

function pct(value: number, digits = 1): string {
  return `${value.toFixed(digits).replace(/\.0+$/, '')}%`;
}

export function getPaycheckFieldExplainers(ctx: PaycheckExplainerContext): FieldExplainerContent[] {
  const { salary, payFrequency, filingStatus, stateRate, contribution401kPercent, healthInsurance, hsa, otherPreTax } =
    ctx.input;
  const {
    periodsPerYear,
    grossPayPerPeriod,
    contribution401kAnnual,
    contribution401kPerPeriod,
    preTaxDeductionsPerPeriod,
    taxableIncomeAnnual,
    standardDeduction,
    federalTaxableIncomeAnnual,
    federalMarginalRate,
    federalBracketLowerBound,
    federalBracketUpperBound,
    federalTaxAnnual,
    federalTaxPerPeriod,
    stateTaxAnnual,
    stateTaxPerPeriod,
    socialSecurityAnnual,
    socialSecurityPerPeriod,
    medicareAnnual,
    medicarePerPeriod,
    additionalMedicareThreshold,
    totalTaxesPerPeriod,
    netPayPerPeriod,
    annualNetPay
  } = ctx.result;

  const frequency = FREQUENCY_LABEL[payFrequency] ?? 'biweekly';
  const filing = FILING_LABEL[filingStatus] ?? 'single';
  const contrastFrequency = payFrequency === 'monthly' ? 'weekly' : 'monthly';
  const contrastPeriods = payFrequency === 'monthly' ? 52 : 12;

  const healthAnnual = healthInsurance * periodsPerYear;
  const hsaAnnual = hsa * periodsPerYear;
  const otherAnnual = otherPreTax * periodsPerYear;

  const marginalPercent = federalMarginalRate * 100;
  const effectiveFederalRate = salary > 0 ? (federalTaxAnnual / salary) * 100 : 0;
  const takeHomeRate = salary > 0 ? (annualNetPay / salary) * 100 : 0;
  const totalTaxAnnual = totalTaxesPerPeriod * periodsPerYear;
  const totalTaxRate = salary > 0 ? (totalTaxAnnual / salary) * 100 : 0;

  const bracketRange =
    Number.isFinite(federalBracketUpperBound)
      ? `${usdWhole.format(federalBracketLowerBound)}–${usdWhole.format(federalBracketUpperBound)}`
      : `${usdWhole.format(federalBracketLowerBound)} and up`;

  // A pre-tax dollar only avoids federal tax while federal taxable income is
  // above zero — when the standard deduction already covers all pay, federal
  // tax is $0 and the nominal 10% bracket shields nothing. State tax here has
  // no standard deduction, so its rate applies while any taxable pay remains.
  const federalShieldRate = federalTaxableIncomeAnnual > 0 && federalTaxAnnual > 0 ? marginalPercent : 0;
  const stateShieldRate = taxableIncomeAnnual > 0 && stateTaxAnnual > 0 ? stateRate : 0;
  const shieldRate = federalShieldRate + stateShieldRate;

  const savingsLine = (annualAmount: number): string =>
    shieldRate > 0
      ? `tax avoided at your rates:\n  ${usdWhole.format(annualAmount)} × (${pct(federalShieldRate)} fed + ${pct(stateShieldRate)} state)\n  ≈ ${usdWhole.format(annualAmount * (shieldRate / 100))} per year${
          federalShieldRate === 0 && marginalPercent > 0
            ? '\n(the standard deduction already zeroes your\nfederal tax, so only state tax is avoided)'
            : ''
        }`
      : 'your taxable pay is already at zero,\nso this buys no further income-tax saving';

  const capped401k = salary * (contribution401kPercent / 100) > DEFERRAL_CAP;
  const ssCapped = salary > SOCIAL_SECURITY_WAGE_BASE;
  const overAdditionalMedicare = salary > additionalMedicareThreshold;

  const federalBlock =
    federalTaxableIncomeAnnual > 0
      ? `taxable pay      ${usdWhole.format(taxableIncomeAnnual)}\n− standard ded.  ${usdWhole.format(standardDeduction)}\n= federal base   ${usdWhole.format(federalTaxableIncomeAnnual)}\n\nyour top bracket: ${pct(marginalPercent)} (${bracketRange})\nfederal tax owed: ${usdWhole.format(federalTaxAnnual)}/yr\neffective on salary: ${pct(effectiveFederalRate)}`
      : `taxable pay      ${usdWhole.format(taxableIncomeAnnual)}\n− standard ded.  ${usdWhole.format(standardDeduction)}\n= federal base   ${usdWhole.format(0)}\n\nthe standard deduction covers all of it,\nso federal income tax is ${usdWhole.format(0)}`;

  return [
    {
      id: 'salary',
      kind: 'input',
      label: 'Annual salary',
      plain:
        'Your gross pay for the year, before anything comes out. Every other figure on this page is carved out of it: pay frequency slices it into checks, pre-tax deductions come off the top, and the four taxes are charged against what is left.',
      formula: `${usdWhole.format(salary)} / ${periodsPerYear} checks\n  = ${usd.format(grossPayPerPeriod)} gross per check`,
      feeds:
        'Gross pay per period, the dollar value of your 401(k) percentage, and both FICA taxes, which are charged on this full amount.',
      assumption:
        'One steady salary for all twelve months. Bonuses, overtime, commission, unpaid leave, and mid-year raises or job changes are not modeled, and every deduction is assumed to repeat identically on every check.'
    },
    {
      id: 'payFrequency',
      kind: 'input',
      label: 'Pay frequency',
      plain:
        'How many paychecks you get in a year. It divides the same annual salary into more or fewer pieces. On its own it changes nothing about your annual tax.',
      formula: `weekly 52 · biweekly 26 · semimonthly 24 · monthly 12\nselected: ${frequency} → ${periodsPerYear} checks\n\n${usdWhole.format(salary)} / ${periodsPerYear} = ${usd.format(grossPayPerPeriod)} per check`,
      feeds: 'Every "per period" figure on the page, and the annual column of the deduction table.',
      assumption:
        healthInsurance > 0
          ? `Health insurance, HSA, and other pre-tax amounts are entered per paycheck, so switching frequency does change their annual total. Your ${usd.format(healthInsurance)} health premium is ${usdWhole.format(healthAnnual)} a year on this ${frequency} schedule; the same figure on a ${contrastFrequency} schedule would be ${usdWhole.format(healthInsurance * contrastPeriods)} a year.`
          : 'Health insurance, HSA, and other pre-tax amounts are entered per paycheck, so switching frequency does change their annual total. If you enter a monthly premium here while paid biweekly, the calculator will deduct it twenty-six times instead of twelve.'
    },
    {
      id: 'filingStatus',
      kind: 'input',
      label: 'Filing status',
      plain:
        'Selects which federal bracket table and standard deduction apply, and where the extra 0.9% Medicare tax kicks in. It does not touch the state calculation here.',
      formula: `${filing}\nstandard deduction ${usdWhole.format(standardDeduction)}\nfederal base ${usdWhole.format(federalTaxableIncomeAnnual)} → ${pct(marginalPercent)} bracket\nextra 0.9% Medicare above ${usdWhole.format(additionalMedicareThreshold)}`,
      feeds: 'Federal income tax, and the Medicare surtax once salary clears the threshold above.',
      assumption: `"Married" here means filing jointly. Married filing separately, dependents, tax credits, itemized deductions, and the withholding adjustments a two-earner household makes on Form W-4 are all outside this model. Bracket and deduction figures are ${TAX_YEAR}.`
    },
    {
      id: 'stateRate',
      kind: 'input',
      label: 'State tax rate',
      plain:
        'One flat percentage applied to your pay after pre-tax deductions. This is deliberately a rough stand-in: the calculator has no state bracket tables, so you supply the rate.',
      formula:
        taxableIncomeAnnual > 0
          ? `${usdWhole.format(taxableIncomeAnnual)} pay after pre-tax deductions\n  × ${pct(stateRate)}\n  = ${usdWhole.format(stateTaxAnnual)} per year\n  = ${usd.format(stateTaxPerPeriod)} per check`
          : `pay after pre-tax deductions is ${usdWhole.format(taxableIncomeAnnual)},\nso the base is floored at ${usdWhole.format(0)}\nstate tax = ${usdWhole.format(0)}`,
      feeds: 'The state tax row, total taxes per period, and net pay.',
      assumption:
        'No state standard deduction, no state brackets, no city or county income tax, and no state-specific treatment of 401(k) or HSA money. Enter the effective rate you actually paid last year (state tax divided by income), not your state’s top marginal rate, or this will overstate the bill. States with no income tax are a 0 here.'
    },
    {
      id: 'contribution401k',
      kind: 'input',
      label: '401(k) contribution',
      plain:
        'The share of gross salary you defer into a traditional 401(k). It lowers the pay that federal and state tax are figured on, but Social Security and Medicare are still charged on your full salary.',
      formula: capped401k
        ? `${usdWhole.format(salary)} × ${pct(contribution401kPercent)} = ${usdWhole.format(salary * (contribution401kPercent / 100))}\ncapped at the ${usdWhole.format(DEFERRAL_CAP)} deferral limit\n  = ${usdWhole.format(contribution401kAnnual)} per year\n  = ${usd.format(contribution401kPerPeriod)} per check`
        : `min(${usdWhole.format(salary)} × ${pct(contribution401kPercent)}, ${usdWhole.format(DEFERRAL_CAP)} cap)\n  = ${usdWhole.format(contribution401kAnnual)} per year\n  = ${usd.format(contribution401kPerPeriod)} per check`,
      feeds: 'The pre-tax deduction bucket, which sets the base for federal and state tax and shrinks net pay.',
      assumption: `Capped at the ${TAX_YEAR} employee deferral limit of ${usdWhole.format(DEFERRAL_CAP)}, with no age-50 catch-up. Employer match is not shown because it does not pass through your paycheck. A Roth 401(k) deferral would take the same money out of your check without lowering taxable pay, so entering one here would flatter your net pay.`
    },
    {
      id: 'healthInsurance',
      kind: 'input',
      label: 'Health insurance per pay period',
      plain:
        'Your share of the premium, withheld from every check before income tax is figured. Enter what comes out of one paycheck, not the annual total.',
      formula: `${usd.format(healthInsurance)} × ${periodsPerYear} = ${usdWhole.format(healthAnnual)} off taxable pay\n${savingsLine(healthAnnual)}`,
      feeds: 'The pre-tax slice of the paycheck chart and the taxable base behind federal and state tax.',
      assumption:
        'Modeled as reducing income tax only. Premiums run through a real Section 125 plan also escape Social Security and Medicare, and this calculator still charges both on your full salary, so it understates the benefit somewhat. Employer-paid premiums are not part of this figure.'
    },
    {
      id: 'hsa',
      kind: 'input',
      label: 'HSA per pay period',
      plain:
        'Money routed to a health savings account out of each check. The math treats it exactly like the health premium: straight off taxable pay before federal and state tax.',
      formula: `${usd.format(hsa)} × ${periodsPerYear} = ${usdWhole.format(hsaAnnual)} off taxable pay\n${savingsLine(hsaAnnual)}`,
      feeds: 'The same pre-tax bucket as premiums, so it reduces federal tax, state tax, and net pay together.',
      assumption:
        'No annual HSA contribution limit is enforced here, and no check that you are on a qualifying high-deductible plan, so the calculator will happily accept more than the IRS allows. As with premiums, payroll HSA money really does avoid FICA but this model still charges it.'
    },
    {
      id: 'otherPreTax',
      kind: 'input',
      label: 'Other pre-tax per pay period',
      plain:
        'A catch-all for anything else withheld before tax: FSA, dependent care, commuter benefits, pre-tax union dues. It lands in the same bucket as premiums and HSA.',
      formula: `${usd.format(otherPreTax)} × ${periodsPerYear} = ${usdWhole.format(otherAnnual)} off taxable pay\n\npre-tax total per check\n  ${usd.format(contribution401kPerPeriod)} 401(k)\n+ ${usd.format(healthInsurance)} health\n+ ${usd.format(hsa)} HSA\n+ ${usd.format(otherPreTax)} other\n= ${usd.format(preTaxDeductionsPerPeriod)}`,
      feeds: 'Pre-tax deductions, which set taxable income for both federal and state tax.',
      assumption:
        'No contribution limits or eligibility rules are checked. Post-tax items do not belong here: Roth contributions, wage garnishments, and after-tax insurance all come out after tax is figured, so entering them here would understate your tax bill.'
    },
    {
      id: 'grossPayPerPeriod',
      kind: 'result',
      label: 'Gross pay per period',
      plain:
        'Salary divided by the number of checks. This is the top of the waterfall: pre-tax deductions come out first, taxes are figured on what remains, and net pay is what survives.',
      formula: `${usdWhole.format(salary)} / ${periodsPerYear} = ${usd.format(grossPayPerPeriod)}\n\n${usd.format(preTaxDeductionsPerPeriod)} pre-tax\n+ ${usd.format(totalTaxesPerPeriod)} taxes\n+ ${usd.format(netPayPerPeriod)} net\n= ${usd.format(grossPayPerPeriod)}`,
      feeds: 'Everything. The three slices of the paycheck pie chart add back to exactly this number.'
    },
    {
      id: 'totalTaxesPerPeriod',
      kind: 'result',
      label: 'Total taxes per period',
      plain:
        'Four separate taxes added up for one paycheck. Federal and state are figured on your pay after pre-tax deductions; Social Security and Medicare are figured on your full salary, which is why deferring more into a 401(k) never shrinks them.',
      formula: `federal    ${usd.format(federalTaxPerPeriod)}\nstate      ${usd.format(stateTaxPerPeriod)}\nSoc. Sec.  ${usd.format(socialSecurityPerPeriod)}\nMedicare   ${usd.format(medicarePerPeriod)}\n         = ${usd.format(totalTaxesPerPeriod)} per check\n\n${usdWhole.format(totalTaxAnnual)} per year, ${pct(totalTaxRate)} of salary`,
      feeds: 'The taxes slice of the chart, the composition table, and net pay.',
      assumption:
        'This estimates tax owed, not what your employer actually withholds. Real withholding follows your Form W-4 and can be noticeably higher or lower; the difference shows up as a refund or a bill at filing time.'
    },
    {
      id: 'federalTax',
      kind: 'result',
      label: 'Federal income tax',
      plain:
        'Progressive bracket math, not a single rate. Your pay after pre-tax deductions is reduced by the standard deduction, then each slice of what remains is taxed at its own bracket rate and the slices are added up.',
      formula: federalBlock,
      feeds: 'The federal row of the deduction table and roughly the largest share of total taxes.',
      assumption:
        federalTaxableIncomeAnnual > 0
          ? `Standard deduction only, ${TAX_YEAR} brackets, and no tax credits of any kind. The ${pct(marginalPercent)} bracket rate applies only to your last dollars; across your whole salary the federal bite works out to ${pct(effectiveFederalRate)}.`
          : `Standard deduction only, ${TAX_YEAR} brackets, and no tax credits of any kind. Nothing here models the refundable credits — the Earned Income Credit above all — that can leave a low earner owing less than zero.`
    },
    {
      id: 'ficaTaxes',
      kind: 'result',
      label: 'Social Security and Medicare',
      plain:
        'The two payroll taxes, both charged on gross salary rather than on pay after deductions. Social Security stops once your wages pass the annual wage base; Medicare never stops, and adds a surcharge above a threshold.',
      formula: `Social Security\n  min(${usdWhole.format(salary)}, ${usdWhole.format(SOCIAL_SECURITY_WAGE_BASE)} wage base) × ${pct(SOCIAL_SECURITY_RATE * 100, 1)}\n  = ${usdWhole.format(socialSecurityAnnual)}/yr${ssCapped ? '  (capped)' : ''}\n\nMedicare\n  ${usdWhole.format(salary)} × ${pct(MEDICARE_RATE * 100, 2)}${overAdditionalMedicare ? `\n  + (${usdWhole.format(salary)} − ${usdWhole.format(additionalMedicareThreshold)}) × ${pct(ADDITIONAL_MEDICARE_RATE * 100, 1)}` : ''}\n  = ${usdWhole.format(medicareAnnual)}/yr`,
      feeds: 'Two rows of the deduction table and part of total taxes per period.',
      assumption: `Employee share only — your employer pays a matching amount you never see. Wage base and thresholds are ${TAX_YEAR} figures. Because both are charged on gross salary here, pre-tax deductions do not reduce them, even though real Section 125 premiums and payroll HSA money would.`
    },
    {
      id: 'netPayPerPeriod',
      kind: 'result',
      label: 'Net pay per period',
      plain: 'What actually reaches your bank account on payday, once deductions and taxes have come out of one gross check.',
      formula: `  ${usd.format(grossPayPerPeriod)} gross\n− ${usd.format(preTaxDeductionsPerPeriod)} pre-tax\n− ${usd.format(totalTaxesPerPeriod)} taxes\n= ${usd.format(netPayPerPeriod)}`,
      feeds: 'The number in the middle of the chart, the net slice, and the annual net pay card.',
      assumption:
        netPayPerPeriod < 0
          ? 'This is negative: the deductions you entered cost more than this paycheck earns. Check whether the per-period amounts were entered as annual figures.'
          : 'No post-tax deductions are modeled, so anything withheld after tax — Roth contributions, life insurance, ESPP purchases, garnishments — comes out of this figure in real life.'
    },
    {
      id: 'annualNetPay',
      kind: 'result',
      label: 'Annual net pay',
      plain:
        'One paycheck multiplied back out across the year. It is not recalculated from annual figures, so it always agrees exactly with the per-period card above.',
      formula: `${usd.format(netPayPerPeriod)} × ${periodsPerYear}\n  = ${usdWhole.format(annualNetPay)}\n  = ${pct(takeHomeRate)} of ${usdWhole.format(salary)} salary`,
      feeds: 'Nothing downstream. It is the annual view of the same paycheck.',
      assumption:
        contribution401kAnnual + hsaAnnual > 0
          ? `${usdWhole.format(contribution401kAnnual + hsaAnnual)} of your salary is missing from this number but not lost — it went into a 401(k) or HSA you own. Take-home pay and total compensation are different questions.`
          : 'Take-home pay is not the same as what the job pays you. With no 401(k) or HSA money set aside, the gap between salary and this figure is entirely tax and insurance.'
    }
  ];
}
